import classNames from 'classnames';
import {Link, WithRouterProps} from 'react-router';
import React, {useEffect, useMemo, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import ClassroomInvitations from './ClassroomInvitations';
import FeatureFlag from 'client/components/FeatureFlag/FeatureFlag.react';
import {getClassroomInvitationsQuery} from 'lib/ClassroomInvitationUtil/ClassroomInvitationUtil';
import {
  HardCapStudentErrorModal,
  InvalidEmailModal
} from 'client/Classrooms/StudentDashboard/Modals';
import {Icon, Tab} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {
  getClassroomsQuery,
  getStudentQuery
} from 'client/Classrooms/StudentDashboard/StudentDashboard.queries.js';
import JoinCodeForm from 'client/Classrooms/StudentDashboard/JoinCodeForm/JoinCodeForm.react';
import {LoadingSpinner} from '@albert-io/atomic';
import StudentHomepageHeader from './StudentHomepageHeader';
import StudentHomepageSidebar from './StudentHomepageSidebar';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

interface TabConfig {
  icon: IconProp;
  label: string;
  to: string;
}

interface RequestConfig {
  beforeRequest: () => void;
  onFulfilled: (value: any) => void;
  onRejected: (reason: any) => void;
  onSettled: () => void;
  query: any;
}

interface Props extends PropsWithChildrenRequired, WithRouterProps {}

export default function StudentHomepage({children, location, params}: Props) {
  const {classroomId} = params;

  const [areClassroomsLoading, setAreClassroomsLoading] = useState(true);
  const [areInvitationsLoading, setAreInvitationsLoading] = useState(true);
  const [isStudentLoading, setIsStudentLoading] = useState(true);

  const {
    exceededHardCapLimit,
    studentHasInvalidEmail,
    invitations,
    setClassrooms,
    setShouldRefetchResources,
    setInvitations,
    setShowJoinClassModal,
    setStudent,
    shouldRefetchResources,
    showJoinClassModal,
    student
  } = useStudentDashboard(
    useShallow((state) => ({
      exceededHardCapLimit: state.exceededHardCapLimit,
      studentHasInvalidEmail: state.studentHasInvalidEmail,
      invitations: state.invitations,
      setClassrooms: state.setClassrooms,
      setShouldRefetchResources: state.setShouldRefetchResources,
      setShowJoinClassModal: state.setShowJoinClassModal,
      setInvitations: state.setInvitations,
      setStudent: state.setStudent,
      shouldRefetchResources: state.shouldRefetchResources,
      showJoinClassModal: state.showJoinClassModal,
      student: state.student
    }))
  );

  const requestConfig: RequestConfig[] = useMemo(
    () =>
      student === null
        ? []
        : [
            {
              beforeRequest: () => {
                setAreClassroomsLoading(true);
              },
              onFulfilled: (value) => {
                setClassrooms(value);
              },
              onRejected: (reason) => {
                console.error(reason);
                // TODO: Add error handling for fetching classrooms
              },
              onSettled: () => {
                setAreClassroomsLoading(false);
              },
              query: getClassroomsQuery(student.getId())
            },
            {
              beforeRequest: () => {
                setAreInvitationsLoading(true);
              },
              onFulfilled: (value) => {
                setInvitations(value);
              },
              onRejected: (reason) => {
                console.error(reason);
                // TODO: Add error handling for fetching invitations
              },
              onSettled: () => {
                setAreInvitationsLoading(false);
              },
              query: getClassroomInvitationsQuery(student.getEmail())
            }
          ],
    [setClassrooms, setInvitations, student]
  );

  // Handles fetching the student. Other requests are made separately in the effect below.
  useEffect(() => {
    getStudentQuery()
      .getResourcePromise()
      .then((fetchedStudent) => {
        setStudent(fetchedStudent);
      })
      .catch((error) => {
        // TODO: Add error state
        console.error(error);
      })
      .finally(() => {
        setIsStudentLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Makes other auxiliary requests once the student has been loaded.
  useEffect(() => {
    if (student !== null) {
      const queries: any[] = [];

      requestConfig.forEach(({beforeRequest, query}) => {
        beforeRequest();

        queries.push(query);
      });

      if (shouldRefetchResources) {
        queries.forEach((query) => {
          query.invalidateInterest();
        });

        setShouldRefetchResources(false);
      }

      const promises = queries.map((query) => query.getResourcePromise());

      Promise.allSettled(promises).then((results) => {
        results.forEach((result, index) => {
          const {onFulfilled, onRejected, onSettled} = requestConfig[index];

          const {status} = result;

          if (status === 'fulfilled') {
            onFulfilled(result.value);
          } else {
            onRejected(result.reason);
          }

          onSettled();
        });
      });
    }
  }, [requestConfig, setShouldRefetchResources, shouldRefetchResources, student]);

  function getToMaybeWithClassroomId(suffix: string) {
    let to = '/home';

    if (classroomId !== undefined) {
      to = `${to}/class/${classroomId}`;
    }

    to = `${to}/${suffix}`;

    return to;
  }

  const tabConfigs: TabConfig[] = [
    {
      icon: ['fal', 'file-alt'],
      label: 'Assignments',
      to: getToMaybeWithClassroomId('assignments')
    },
    {
      icon: ['fal', 'sparkles'],
      label: 'Skill Path',
      to: getToMaybeWithClassroomId('skill-path')
    },
    {
      icon: ['fal', 'pencil'],
      label: 'Independent Practice',
      to: getToMaybeWithClassroomId('independent-practice')
    }
  ];

  return (
    <FeatureFlag name='new_student_dashboard'>
      <div className='-mt-[60px] grid min-h-[calc(100vh-60px)] grid-cols-4 grid-rows-[repeat(2,min-content)_1fr] background-subtle'>
        {isStudentLoading ? (
          <LoadingSpinner className='col-span-4 row-span-3 self-center' size={5} />
        ) : (
          <>
            <StudentHomepageHeader />
            <StudentHomepageSidebar isLoading={areClassroomsLoading} />
            {(areInvitationsLoading || invitations.size > 0) && (
              <div className='col-span-3'>
                {areInvitationsLoading ? (
                  <LoadingSpinner bottomMarginLevel={0} size={2} />
                ) : (
                  <ClassroomInvitations className='col-span-3 mx-x2 mt-x4 mb-x0' />
                )}
              </div>
            )}
            <div
              className={classNames(
                'col-span-3 col-start-2 mx-x2 my-x3 rounded-3xl background-primary',
                {
                  'row-span-2': invitations.size === 0
                }
              )}
            >
              <div className='flex items-start'>
                {tabConfigs.map(({icon, label, to}) => (
                  <Tab
                    as={Link}
                    className='px-x4 py-x3'
                    contentWrapperClassName='flex gap-x1 items-center'
                    isActive={location.pathname.includes(to)}
                    key={to}
                    to={to}
                  >
                    <Icon icon={icon} />
                    {label}
                  </Tab>
                ))}
              </div>
              <div className='flex h-full items-center justify-center'>{children}</div>
            </div>
            {studentHasInvalidEmail && <InvalidEmailModal />}
            {exceededHardCapLimit && <HardCapStudentErrorModal />}
            {showJoinClassModal && (
              <JoinCodeForm.Modal
                handleClose={() => setShowJoinClassModal(false)}
                isNewStudentDashboard
                onEnrollSuccess={() => setShowJoinClassModal(false)}
              />
            )}
          </>
        )}
      </div>
    </FeatureFlag>
  );
}
